import React from 'react';
import keycode from 'keycode';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { colors, fonts } from 'bench-style-constants';
import { Fill } from 'layout-core-components';
import { Text } from 'primitive-components';
import FloatingActions from 'floating-actions';

import { addLeftPadding } from './keyframes';

const StyledText = styled(Text)`
  padding: 8px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 24px;
  margin: 0;
`;

const animationRule = css`
  ${addLeftPadding('16px')} 0.2s ease-out forwards;
`;

const StyledTextField = styled.input.attrs(() => ({
  type: 'text',
}))`
  animation: ${animationRule};
  padding: 8px 16px;
  background: ${colors.shell};
  border: 0;
  border-radius: 0.25rem;
  color: ${colors.charcoal};
  font-family: ${fonts.circular};
  font-size: 16px;
  font-weight: 300;
  line-height: 24px;
  transition: background 0.25s ease-in-out, color 0.25s ease-in-out;
  width: 100%;

  &:focus {
    outline: 0;
    box-shadow: inset 0 0 0 1px ${colors.stream};
  }

  &::placeholder {
    color: ${colors.dust};
  }
`;

const StyledEditableTextWrapper = styled.div`
  &:hover ${StyledText} {
    padding-right: 3.5rem;
  }

  &:hover ${StyledTextField}, &:focus-within ${StyledTextField} {
    padding-right: 4.5rem;
  }
`;

class EditableText extends React.Component {
  static propTypes = {
    maxLength: PropTypes.number,
    value: PropTypes.string.isRequired,
    onChangeSubmitted: PropTypes.func.isRequired,
    onDeleteRequested: PropTypes.func,
  };

  static defaultProps = {
    maxLength: 256,
    onDeleteRequested: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      editing: false,
      draftValue: this.props.value,
      savedValue: this.props.value,
    };
    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleBlur(event) {
    /**
     * Fallback to use Firefox native legacy property `event.explicitOriginalTarget` for
     * cases where `relatedTarget` is set to null.
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Event/Comparison_of_Event_Targets
     * @link https://github.com/facebook/react/issues/2011
     */
    const relatedTarget = event.relatedTarget || event.nativeEvent.explicitOriginalTarget;
    if (event.currentTarget.contains(relatedTarget)) return;
    this.toggleEditMode();
  }

  handleChange(event) {
    this.setState({ draftValue: event.target.value });
  }

  handleKeyDown(event) {
    if (keycode(event) === 'enter') {
      this.saveChanges();
    }
  }

  toggleEditMode() {
    this.setState({
      draftValue: this.state.savedValue,
      editing: !this.state.editing,
    });
  }

  saveChanges() {
    const { draftValue, savedValue } = this.state;
    if (draftValue !== savedValue) {
      this.setState({ savedValue: draftValue });
      this.props.onChangeSubmitted(draftValue);
    }
    this.toggleEditMode();
  }

  render() {
    if (this.state.editing) {
      return (
        <StyledEditableTextWrapper onBlur={this.handleBlur}>
          <FloatingActions>
            <Fill width="parent">
              <StyledTextField
                autoFocus
                autoComplete="off"
                maxLength={this.props.maxLength}
                name="value"
                onChange={this.handleChange}
                onKeyDown={this.handleKeyDown}
                value={this.state.draftValue}
              />
            </Fill>
            <FloatingActions.List transition padRight>
              <FloatingActions.Item
                data-test-id="editable-text_save"
                icon="checkmark"
                onClick={() => this.saveChanges()}
              />
              <FloatingActions.Item
                styleType="danger"
                data-test-id="editable-text_cancel"
                icon="cancel"
                onClick={() => this.toggleEditMode()}
              />
            </FloatingActions.List>
          </FloatingActions>
        </StyledEditableTextWrapper>
      );
    }

    return (
      <StyledEditableTextWrapper>
        <FloatingActions>
          <StyledText onClick={() => this.toggleEditMode()}>{this.props.value}</StyledText>
          <FloatingActions.List>
            <FloatingActions.Item
              icon="pencil"
              data-test-id="editable-text_edit"
              onClick={() => this.toggleEditMode()}
            />
            {this.props.onDeleteRequested && (
              <FloatingActions.Item
                styleType="danger"
                icon="trash"
                data-test-id="editable-text_delete"
                onClick={this.props.onDeleteRequested}
              />
            )}
          </FloatingActions.List>
        </FloatingActions>
      </StyledEditableTextWrapper>
    );
  }
}

export default EditableText;
